/**
 * @module ReasoningService
 * @purpose API client functions for the five reasoning-engine endpoints.
 * @depends apiClient (services/api.ts), reasoning types (types/reasoning.ts)
 * @dependedOnBy hooks/use-reasoning.ts
 *
 * @rules
 * - Performance: stateless service functions; state lives in hooks
 * - State: stateless
 * - Errors: ApiError propagates from apiClient — no try-catch swallowing
 */

import { ApiError, apiClient } from './api'
import type {
  ReasoningRun,
  ClusterSummary,
  ClusterDetail,
  ResolvedObservation,
  EvaluateHypothesesResponse,
  HypothesesListResponse,
  Hypothesis,
  HypothesisLibraryResponse,
} from '../types/reasoning'

/**
 * POST /api/cases/:caseId/reasoning-runs — create or reuse a reasoning run.
 *
 * @behaviour Passes ?force=true to bypass reuse logic and trigger a fresh computation.
 * @errors Propagates ApiError from apiClient (401, 403, 404, 500).
 */
export async function createReasoningRun(
  caseId: string,
  options: { force?: boolean } = {},
): Promise<ReasoningRun> {
  const qs = options.force ? '?force=true' : ''
  return apiClient.post<ReasoningRun>(`/cases/${caseId}/reasoning-runs${qs}`)
}

/**
 * GET /api/cases/:caseId/reasoning-runs/latest — fetch the most recent run record.
 *
 * @behaviour Returns the run in whatever status it is in (pending, running, completed, failed).
 * @errors Propagates ApiError from apiClient (401, 403, 404, 500).
 */
export async function getLatestReasoningRun(caseId: string): Promise<ReasoningRun | null> {
  try {
    return await apiClient.get<ReasoningRun>(`/cases/${caseId}/reasoning-runs/latest`)
  } catch (err) {
    // A case that has never had a reasoning run is an EMPTY state, not a
    // failure. Treating the 404 as an error rendered "Reasoning failed" with
    // no way to start a first run, which is what every fresh tenant meets.
    if (err instanceof ApiError && err.isNotFound) return null
    throw err
  }
}

/**
 * GET /api/cases/:caseId/reasoning-runs/:runId/clusters — paginated cluster list.
 *
 * @behaviour offset-paginated; total reflects the complete count for the run.
 * @errors Propagates ApiError from apiClient (401, 403, 404, 500).
 */
export async function listClusters(
  caseId: string,
  runId: string,
  params: { limit?: number; offset?: number } = {},
): Promise<{ clusters: ClusterSummary[]; total: number }> {
  const sp = new URLSearchParams()
  if (params.limit !== undefined) sp.set('limit', String(params.limit))
  if (params.offset !== undefined) sp.set('offset', String(params.offset))
  const qs = sp.toString() ? `?${sp.toString()}` : ''
  return apiClient.get(`/cases/${caseId}/reasoning-runs/${runId}/clusters${qs}`)
}

/**
 * GET /api/cases/:caseId/reasoning-runs/:runId/clusters/:clusterId — full cluster detail.
 *
 * @behaviour Includes enrichments (lolbas, shimcache, temporal) and explanation lines.
 * @errors Propagates ApiError from apiClient (401, 403, 404, 500).
 */
export async function getCluster(
  caseId: string,
  runId: string,
  clusterId: string,
): Promise<ClusterDetail> {
  return apiClient.get<ClusterDetail>(
    `/cases/${caseId}/reasoning-runs/${runId}/clusters/${clusterId}`,
  )
}

/**
 * POST /api/cases/:caseId/reasoning-runs/:runId/observations/resolve — batch observation resolution.
 *
 * @behaviour Accepts up to 200 observation IDs per call; returns provenance for each found row.
 * @errors Propagates ApiError from apiClient (401, 403, 404, 500).
 */
export async function resolveObservations(
  caseId: string,
  runId: string,
  observationIds: string[],
): Promise<{ resolved: ResolvedObservation[] }> {
  return apiClient.post(`/cases/${caseId}/reasoning-runs/${runId}/observations/resolve`, {
    observationIds,
  })
}

/**
 * POST /api/cases/:caseId/reasoning-runs/:runId/stage6-evaluate — trigger Stage-6 predicate evaluation.
 *
 * @behaviour Default (no predicate param): evaluates EVERY library predicate in library
 *   order. Pass predicate to narrow to one (version optional — resolved from the library;
 *   version without predicate is a 400). Idempotent when force=false and prior results
 *   exist (per predicate). Pass force=true to re-evaluate and overwrite existing results.
 * @errors Propagates ApiError from apiClient (400 blank params, 403 access, 404 run/predicate, 422 run not completed, 500).
 */
export async function evaluateHypotheses(
  caseId: string,
  runId: string,
  opts: { predicate?: string; version?: string; force?: boolean } = {},
): Promise<EvaluateHypothesesResponse> {
  const sp = new URLSearchParams()
  if (opts.predicate !== undefined) sp.set('predicate', opts.predicate)
  if (opts.version !== undefined) sp.set('version', opts.version)
  if (opts.force !== undefined) sp.set('force', String(opts.force))
  const qs = sp.toString() ? `?${sp.toString()}` : ''
  return apiClient.post<EvaluateHypothesesResponse>(
    `/cases/${caseId}/reasoning-runs/${runId}/stage6-evaluate${qs}`,
  )
}

/**
 * GET /api/cases/:caseId/reasoning-runs/:runId/stage6-hypotheses — list all hypotheses for a run.
 *
 * @behaviour Returns empty hypotheses array with zero counts when no evaluation has run.
 *   Never 404 on an empty result set.
 * @errors Propagates ApiError from apiClient (403, 404, 500).
 */
export async function listHypotheses(
  caseId: string,
  runId: string,
): Promise<HypothesesListResponse> {
  return apiClient.get<HypothesesListResponse>(
    `/cases/${caseId}/reasoning-runs/${runId}/stage6-hypotheses`,
  )
}

/**
 * GET /api/cases/:caseId/reasoning-runs/:runId/stage6-hypotheses/:hypothesisId — single hypothesis.
 *
 * @behaviour Returns the full hypothesis including typed explanation and raw evidence inventory.
 * @errors Propagates ApiError from apiClient (403, 404, 500).
 */
export async function getHypothesis(
  caseId: string,
  runId: string,
  hypothesisId: string,
): Promise<Hypothesis> {
  return apiClient.get<Hypothesis>(
    `/cases/${caseId}/reasoning-runs/${runId}/stage6-hypotheses/${hypothesisId}`,
  )
}

/**
 * GET /api/hypothesis-library — full platform hypothesis library, independent of case state.
 *
 * @behaviour Global endpoint (no caseId/runId): every registered predicate with descriptive
 *   metadata, already in library order.
 * @errors Propagates ApiError from apiClient (401 no token, 500 invalid role).
 */
export async function listHypothesisLibrary(): Promise<HypothesisLibraryResponse> {
  return apiClient.get<HypothesisLibraryResponse>('/hypothesis-library')
}
